import { MAPS_API_KEY } from './constants';
import { abbreviateState, formatCityState } from './geo';

let mapsPromise = null;

export function hasMapsKey() {
  return Boolean(MAPS_API_KEY);
}

export function prefersDarkMode() {
  return typeof window !== 'undefined'
    && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/** Matches Android `map_style_sello_dark.json` / light POI-off baseline. */
export function googleMapStyles(dark = prefersDarkMode()) {
  if (!dark) {
    return [{ featureType: 'poi', stylers: [{ visibility: 'off' }] }];
  }
  return [
    { elementType: 'geometry', stylers: [{ color: '#000000' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#F8FAFC' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#0F172A' }] },
    { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#334155' }] },
    { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#12161F' }] },
    { featureType: 'poi', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#14532D' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#12161F' }] },
    { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#1A2030' }] },
    { featureType: 'road', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#1E3A8A' }] },
    { featureType: 'transit', stylers: [{ visibility: 'off' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0C4A6E' }] },
  ];
}

export function loadGoogleMaps() {
  if (!MAPS_API_KEY) return Promise.reject(new Error('Missing Google Maps API key'));
  if (window.google?.maps?.places) return Promise.resolve(window.google.maps);
  if (mapsPromise) return mapsPromise;

  mapsPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-sosello-maps]');
    if (existing) {
      existing.addEventListener('load', () => resolve(window.google.maps));
      existing.addEventListener('error', () => reject(new Error('Failed to load Google Maps')));
      return;
    }
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(MAPS_API_KEY)}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.dataset.soselloMaps = '1';
    script.onload = () => resolve(window.google.maps);
    script.onerror = () => reject(new Error('Failed to load Google Maps'));
    document.head.appendChild(script);
  });

  return mapsPromise;
}

export function placeFromGeocoderResult(result) {
  const components = result.address_components || [];
  const find = (type) => components.find((c) => c.types.includes(type));
  const city =
    find('locality')?.long_name ||
    find('sublocality')?.long_name ||
    find('postal_town')?.long_name ||
    find('administrative_area_level_2')?.long_name ||
    '';
  const stateRaw = find('administrative_area_level_1')?.long_name || '';
  const state = abbreviateState(stateRaw);
  const lat = result.geometry.location.lat();
  const lng = result.geometry.location.lng();
  return {
    lat,
    lng,
    city,
    state,
    label: formatCityState(city, state) || result.formatted_address || 'Selected location',
  };
}

export async function reverseGeocode(lat, lng) {
  const maps = await loadGoogleMaps();
  const geocoder = new maps.Geocoder();
  const { results } = await geocoder.geocode({ location: { lat, lng } });
  if (!results?.length) {
    return { lat, lng, city: '', state: '', label: `${lat.toFixed(2)}, ${lng.toFixed(2)}` };
  }
  return placeFromGeocoderResult(results[0]);
}

export async function searchPlaces(query) {
  if (!query.trim()) return [];
  const maps = await loadGoogleMaps();
  const service = new maps.places.AutocompleteService();
  const predictions = await new Promise((resolve) => {
    service.getPlacePredictions(
      { input: query, types: ['(regions)'] },
      (items) => resolve(items || []),
    );
  });
  return predictions.slice(0, 6).map((p) => ({
    id: p.place_id,
    label: p.description,
    placeId: p.place_id,
  }));
}

export async function resolvePlaceId(placeId) {
  const maps = await loadGoogleMaps();
  const service = new maps.places.PlacesService(document.createElement('div'));
  const place = await new Promise((resolve, reject) => {
    service.getDetails(
      { placeId, fields: ['address_component', 'geometry', 'formatted_address', 'name'] },
      (result, status) => {
        if (status !== maps.places.PlacesServiceStatus.OK || !result) {
          reject(new Error('Could not resolve place'));
          return;
        }
        resolve(result);
      },
    );
  });
  return placeFromGeocoderResult(place);
}

export function staticMapUrl(lat, lng, { width = 640, height = 320, zoom = 12 } = {}) {
  if (!MAPS_API_KEY) return '';
  const dark = prefersDarkMode();
  const center = `${lat},${lng}`;
  const marker = dark ? '0x5B8DFF' : '0x276CFF';
  return `https://maps.googleapis.com/maps/api/staticmap?center=${center}&zoom=${zoom}&size=${width}x${height}&scale=2&markers=color:${marker}%7C${center}&key=${encodeURIComponent(MAPS_API_KEY)}&style=feature:poi%7Cvisibility:off`;
}

export function googleMapsDirectionsUrl(lat, lng) {
  return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
}
